// Follow-ups — every contact we delivered a demo to, as chat threads.
// AI drafts the follow-up in the owning rep's voice; sends go out from
// that rep's mailbox and land in the thread.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  MessagesSquare, RefreshCw, Sparkles, Send, Eye, EyeOff, Search,
  ExternalLink, ChevronLeft, XCircle,
} from 'lucide-react';
import { apiService } from '../services/apiService';
import { Chip, Btn, inputStyle, useToasts, EmptyState, Spinner, ConfirmDialog, fmtDate, fmtDateTime } from './ui';

// Server timestamps are UTC without a zone marker; read them as UTC.
const toDate = (iso) => (iso ? new Date(/[zZ]$|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`) : null);
const relDays = (iso) => {
  const t = toDate(iso);
  if (!t) return null;
  const d = Math.floor((Date.now() - t.getTime()) / 86400000);
  return d <= 0 ? 'today' : d === 1 ? 'yesterday' : `${d}d ago`;
};
// Where each contact stands:
//   you  = they wrote last, we owe a reply
//   due  = our last message is 2+ days old with no answer: time to follow up
//   them = we replied in the last 2 days: nothing to do yet
const statusOf = (c) => {
  if (c.in_deal) return 'deal';
  if (c.awaiting_us) return 'you';
  const lt = c.last_touch;
  const t = toDate(lt?.at || c.demo_sent_at);
  if (lt && lt.direction !== 'inbound' && t && (Date.now() - t.getTime()) < 2 * 86400000) return 'them';
  return 'due';
};
const GROUPS = [['you', 'Waiting on you'], ['due', 'Follow-up due'], ['them', 'You replied · waiting on them']];
const STATUS_RANK = { you: 0, due: 1, them: 2, deal: 3 };
const tsOf = (c) => { const t = toDate(c.last_touch?.at || c.demo_sent_at); return t ? t.getTime() : 0; };

const Followups = () => {
  const [contacts, setContacts] = useState([]);
  const [sortBy, setSortBy] = useState(() => { try { return localStorage.getItem('fu_sort') || 'action'; } catch { return 'action'; } });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | awaiting | opened | unopened
  const [q, setQ] = useState('');
  const [active, setActive] = useState(null); // lead_email
  const [thread, setThread] = useState(null); // {loading, messages, demo, rep_name, owner_mailbox}
  const [subject, setSubject] = useState('');
  const [draft, setDraft] = useState('');
  const [drafting, setDrafting] = useState(false);
  const [stage, setStage] = useState(null);
  const [sending, setSending] = useState(false);
  const [confirmLost, setConfirmLost] = useState(false);
  const [context, setContext] = useState(''); // rep's note for the AI draft
  const [why, setWhy] = useState(null); // the model's read of the situation
  const [timing, setTiming] = useState(null); // {send_now, advice}
  const QUICK = ['They did not show up', 'They cancelled the meeting', 'They skipped the demo walkthrough', 'Gentle nudge, no pressure', 'They asked for pricing', 'Re-engage after a long silence', 'Recap what the demo does for them'];
  const [push, toasts] = useToasts();
  const bottomRef = useRef(null);
  const isMobile = window.innerWidth < 900;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.getFollowupDemos();
      setContacts(res.contacts || []);
    } catch (e) {
      push(`Failed to load: ${e.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [push]);

  useEffect(() => { load(); }, [load]);

  const openThread = useCallback(async (email) => {
    setActive(email);
    setDraft(''); setSubject(''); setStage(null); setContext(''); setWhy(null); setTiming(null);
    setThread({ loading: true });
    try {
      const res = await apiService.getFollowupThread(email);
      setThread({ loading: false, ...res });
      setTimeout(() => bottomRef.current?.scrollIntoView({ block: 'end' }), 60);
      // A draft prepared overnight for this lead? Put it in the composer, ready to review and send.
      try {
        const d = await apiService.getFollowupDraft(email);
        if (d?.draft?.body) {
          setDraft(d.draft.body); setSubject(d.draft.subject || '');
          setStage(d.draft.stage ? `prepared draft · ${d.draft.stage}` : 'prepared draft');
          push('A draft is ready for this lead. Review it, make it yours, and send.');
        }
      } catch {}
    } catch (e) {
      setThread({ loading: false, error: e.message });
    }
  }, [push]);

  const genDraft = async () => {
    setDrafting(true);
    try {
      const res = await apiService.generateFollowup(active, context.trim());
      if (res.error) throw new Error(res.error);
      setDraft(res.body || '');
      setSubject(res.subject || '');
      setStage(res.stage ? res.stage + ' (touch ' + res.touches + ')' : null);
      setWhy(res.analysis || null);
      setTiming(res.timing || null);
      push(`Draft written in ${res.rep_name || 'the rep'}'s voice${res.model ? ' · ' + res.model : ''}`);
    } catch (e) {
      push(`AI draft failed: ${e.message}`, 'error');
    } finally {
      setDrafting(false);
    }
  };

  const sendMsg = async () => {
    if (!draft.trim()) return;
    setSending(true);
    const sentText = draft;
    try {
      const res = await apiService.sendFollowup(active, subject, draft);
      if (res.error) throw new Error(res.error);
      push(`Sent from ${res.sent_via} · moved to "You replied"`);
      setContacts((prev) => prev.map((c) => (c.lead_email === active ? {
        ...c, awaiting_us: false, has_draft: false,
        last_touch: { direction: 'outbound', at: new Date().toISOString(), snippet: sentText.replace(/\s+/g, ' ').slice(0, 160), is_auto: false },
      } : c)));
      setDraft(''); setSubject('');
      openThread(active);
      load();
    } catch (e) {
      push(`Send failed: ${e.message}`, 'error');
    } finally {
      setSending(false);
    }
  };

  const markLost = async () => {
    setConfirmLost(false);
    try {
      const res = await apiService.markFollowupLost(active);
      if (res.error) throw new Error(res.error);
      push('Marked as lost and removed from the list');
      setActive(null); setThread(null);
      load();
    } catch (e) {
      push(`Failed: ${e.message}`, 'error');
    }
  };

  const shown = contacts.filter((c) => {
    if (filter === 'deal') { if (!c.in_deal) return false; }
    else if (c.in_deal) return false;            // people we've met are handled personally, not in this queue
    if (filter === 'noreply' && !c.demo_no_reply) return false;
    if (['you', 'due', 'them'].includes(filter) && statusOf(c) !== filter) return false;
    if (filter === 'awaiting' && !c.awaiting_us) return false;
    if (filter === 'opened' && !c.open_count) return false;
    if (filter === 'unopened' && c.open_count) return false;
    if (q && !(`${c.company} ${c.lead_email} ${c.lead_name}`.toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  });
  const ordered = [...shown].sort((a, b) => {
    if (sortBy === 'recent') return tsOf(b) - tsOf(a);
    if (sortBy === 'oldest') return tsOf(a) - tsOf(b);
    if (sortBy === 'opens') return (b.open_count || 0) - (a.open_count || 0) || tsOf(b) - tsOf(a);
    if (sortBy === 'az') return (a.company || a.lead_email).localeCompare(b.company || b.lead_email);
    const ra = STATUS_RANK[statusOf(a)], rb = STATUS_RANK[statusOf(b)];
    if (ra !== rb) return ra - rb;
    // waiting on you / due: longest waiting first; you replied: most recent first
    return statusOf(a) === 'them' ? tsOf(b) - tsOf(a) : tsOf(a) - tsOf(b);
  });
  const grouped = sortBy === 'action' && filter === 'all';
  const counts = contacts.reduce((m, c) => {
    const k = statusOf(c); m[k] = (m[k] || 0) + 1;
    if (!c.in_deal && c.demo_no_reply) m.noreply = (m.noreply || 0) + 1;
    return m;
  }, {});
  const setDeal = async (email, status) => {
    try {
      const res = await apiService.setFollowupStatus(email, status);
      if (res.error) throw new Error(res.error);
      setContacts((prev) => prev.map((c) => (c.lead_email === email ? { ...c, in_deal: status === 'in_deal', deal_reason: status === 'in_deal' ? 'marked as a deal' : null } : c)));
      push(status === 'in_deal' ? 'Moved to "Met / in a deal" — out of the follow-up queue' : 'Back in the follow-up queue');
    } catch (e) {
      push(`Could not update: ${e.message}`, 'error');
    }
  };
  const renderRow = (c) => (
              <div key={c.lead_email} onClick={() => openThread(c.lead_email)} style={{
                padding: '11px 14px', borderBottom: '1px solid var(--c-border)', cursor: 'pointer',
                background: active === c.lead_email ? 'var(--c-primary-glow)' : 'transparent',
                borderLeft: `3px solid ${statusOf(c) === 'you' ? 'var(--c-yellow)' : statusOf(c) === 'due' ? 'var(--c-primary)' : 'transparent'}`,
                opacity: statusOf(c) === 'them' && active !== c.lead_email ? 0.7 : 1,
              }}
              onMouseEnter={(e) => { if (active !== c.lead_email) e.currentTarget.style.background = 'var(--c-surface-hover)'; }}
              onMouseLeave={(e) => { if (active !== c.lead_email) e.currentTarget.style.background = 'transparent'; }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 3 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--c-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.company || c.lead_email}</span>
                  <span style={{ fontSize: 10.5, color: 'var(--c-text-3)', whiteSpace: 'nowrap' }}>{relDays(c.last_touch?.at) || relDays(c.demo_sent_at)}</span>
                </div>
                <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Chip tone={c.open_count > 0 ? 'green' : 'gray'} style={{ fontSize: 10 }}>
                    {c.open_count > 0 ? <><Eye size={10} /> {c.open_count}</> : <><EyeOff size={10} /> demo</>}
                  </Chip>
                  {c.awaiting_us && <Chip tone="yellow" style={{ fontSize: 10 }}>they replied</Chip>}
                  {statusOf(c) === 'them' && <Chip tone="blue" style={{ fontSize: 10 }}>✓ you replied</Chip>}
                  {c.deal_reason && <Chip tone="purple" style={{ fontSize: 10 }}>{c.deal_reason}</Chip>}
                  {!c.in_deal && c.demo_no_reply && <Chip tone="red" style={{ fontSize: 10 }}>no reply to demo</Chip>}
                  {c.has_draft && <Chip tone="green" style={{ fontSize: 10 }}>draft ready</Chip>}
                  {c.no_demo && <Chip tone="gray" style={{ fontSize: 10 }}>no demo</Chip>}
                  {!c.awaiting_us && c.last_touch?.is_auto && <Chip tone="blue" style={{ fontSize: 10 }}>auto-ack</Chip>}
                  <span style={{ fontSize: 10.5, color: 'var(--c-text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140 }}>
                    {(c.sent_via || '').split('@')[0]}
                  </span>
                </div>
                {c.last_touch?.snippet && (
                  <div style={{ fontSize: 11.5, color: 'var(--c-text-2)', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.last_touch.direction === 'inbound' ? '↩ ' : '→ '}{c.last_touch.snippet}
                  </div>
                )}
              </div>
);
  const activeContact = contacts.find((c) => c.lead_email === active);
  const showList = !isMobile || !active;
  const showChat = !isMobile || !!active;

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', padding: '20px 20px 40px' }}>
      <div style={{ display: 'flex', gap: 16, height: 'calc(100vh - 130px)', minHeight: 480 }}>

        {/* Contact list */}
        {showList && (
        <div style={{
          width: isMobile ? '100%' : 360, flexShrink: 0, display: 'flex', flexDirection: 'column',
          background: 'var(--c-bg-1)', border: '1px solid var(--c-border)', borderRadius: 14, overflow: 'hidden',
        }}>
          <div style={{ padding: 14, borderBottom: '1px solid var(--c-border)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: 16, color: 'var(--c-text)' }}>
                Demo contacts <span style={{ color: 'var(--c-text-3)', fontWeight: 500 }}>({shown.length})</span>
              </div>
              <Btn small onClick={load}><RefreshCw size={12} /></Btn>
            </div>
            <div style={{ position: 'relative', marginBottom: 8 }}>
              <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--c-text-3)' }} />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
                style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', paddingLeft: 30, padding: '7px 10px 7px 30px', fontSize: 12.5 }} />
            </div>
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
              {[['all', 'All'], ['noreply', `No reply to demo${counts.noreply ? ` (${counts.noreply})` : ''}`], ['you', `Waiting on you${counts.you ? ` (${counts.you})` : ''}`], ['due', `Follow-up due${counts.due ? ` (${counts.due})` : ''}`], ['them', `You replied${counts.them ? ` (${counts.them})` : ''}`], ['deal', `Met / in a deal${counts.deal ? ` (${counts.deal})` : ''}`]].map(([k, label]) => (
                <button key={k} onClick={() => setFilter(k)} style={{
                  padding: '4px 10px', borderRadius: 999, fontSize: 11.5, fontWeight: 600, cursor: 'pointer',
                  fontFamily: "'Inter', sans-serif",
                  border: filter === k ? '1px solid var(--c-border-bright)' : '1px solid var(--c-border)',
                  background: filter === k ? 'var(--c-primary-glow)' : 'transparent',
                  color: filter === k ? 'var(--c-primary)' : 'var(--c-text-2)',
                }}>{label}</button>
              ))}
            </div>
            <select value={sortBy} onChange={(e) => { setSortBy(e.target.value); try { localStorage.setItem('fu_sort', e.target.value); } catch {} }}
              style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', marginTop: 8, fontSize: 12, padding: '6px 10px' }}>
              <option value="action">Sort: needs action first</option>
              <option value="recent">Sort: latest activity first</option>
              <option value="oldest">Sort: longest quiet first</option>
              <option value="opens">Sort: most demo views</option>
              <option value="az">Sort: company A–Z</option>
            </select>
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loading && <div style={{ padding: 40, textAlign: 'center' }}><Spinner size={20} /></div>}
            {!loading && shown.length === 0 && <EmptyState icon={MessagesSquare} title="No contacts match" />}
            {!loading && (grouped
              ? GROUPS.map(([g, label]) => {
                  const rows = ordered.filter((c) => statusOf(c) === g);
                  if (!rows.length) return null;
                  return (
                    <div key={g}>
                      <div style={{ position: 'sticky', top: 0, zIndex: 1, padding: '6px 14px', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
                        color: g === 'you' ? 'var(--c-yellow)' : g === 'due' ? 'var(--c-primary)' : 'var(--c-text-3)', background: 'var(--c-bg-2, var(--c-bg-1))', borderBottom: '1px solid var(--c-border)' }}>
                        {label} · {rows.length}
                      </div>
                      {rows.map(renderRow)}
                    </div>
                  );
                })
              : ordered.map(renderRow))}
          </div>
        </div>
        )}

        {/* Chat pane */}
        {showChat && (
        <div style={{
          flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column',
          background: 'var(--c-bg-1)', border: '1px solid var(--c-border)', borderRadius: 14, overflow: 'hidden',
        }}>
          {!active && (
            <EmptyState icon={MessagesSquare} title="Pick a contact" sub="Their full conversation opens here, with AI follow-up drafting." />
          )}
          {active && (
            <>
              {/* Header */}
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--c-border)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                {isMobile && (
                  <button onClick={() => { setActive(null); setThread(null); }} style={{ background: 'none', border: 'none', color: 'var(--c-text-2)', cursor: 'pointer', padding: 4 }}>
                    <ChevronLeft size={18} />
                  </button>
                )}
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--c-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {activeContact?.company || active}
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--c-text-3)' }}>
                    {active} · rep: {thread?.rep_name || activeContact?.sent_via || '…'}
                  </div>
                </div>
                {thread?.demo?.demo_url && (
                  <a href={thread.demo.demo_url} target="_blank" rel="noreferrer" style={{ color: 'var(--c-primary)', fontSize: 12, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    demo <ExternalLink size={11} />
                  </a>
                )}
                {thread?.demo && (
                  <Chip tone={thread.demo.open_count > 0 ? 'green' : 'gray'}>
                    <Eye size={11} /> {thread.demo.open_count} views (incl. team)
                  </Chip>
                )}
                {activeContact?.in_deal
                  ? <Btn small onClick={() => setDeal(active, 'queue')} title="Put this contact back in the follow-up queue">Back to follow-ups</Btn>
                  : <Btn small onClick={() => setDeal(active, 'in_deal')} title="We've met / this is an active deal: stop follow-up nudges">Met / in a deal</Btn>}
                <Btn small tone="danger" onClick={() => setConfirmLost(true)} title="Mark lost and remove from this list">
                  <XCircle size={12} /> Lost
                </Btn>
              </div>

              {/* Messages */}
              <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
                {thread?.loading && <div style={{ padding: 40, textAlign: 'center' }}><Spinner size={20} /></div>}
                {thread?.error && <div style={{ color: 'var(--c-red)', fontSize: 13 }}>{thread.error}</div>}
                {thread?.demo?.sent_at && (
                  <div style={{ textAlign: 'center', margin: '4px 0 14px' }}>
                    <Chip tone="blue">📦 demo delivered {fmtDate(thread.demo.sent_at)} via {(thread.demo.sent_via || '').split('@')[0]}</Chip>
                  </div>
                )}
                {(thread?.messages || []).map((m, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: m.direction === 'inbound' ? 'flex-start' : 'flex-end', marginBottom: 10 }}>
                    <div style={{
                      maxWidth: '78%', padding: '10px 13px', borderRadius: 14, fontSize: 13, lineHeight: 1.5,
                      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      background: m.direction === 'inbound' ? 'var(--c-surface)' : 'var(--c-primary-glow)',
                      border: `1px solid ${m.direction === 'inbound' ? 'var(--c-border)' : 'var(--c-border-bright)'}`,
                      color: 'var(--c-text)',
                      borderBottomLeftRadius: m.direction === 'inbound' ? 4 : 14,
                      borderBottomRightRadius: m.direction === 'inbound' ? 14 : 4,
                    }}>
                      {m.body}
                      <div style={{ fontSize: 10, color: 'var(--c-text-3)', marginTop: 6 }}>
                        {m.direction === 'inbound' ? (activeContact?.lead_name || 'them') : (m.from_email || 'us').split('@')[0]} · {fmtDateTime(m.at)}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>

              {/* Composer */}
              <div style={{ borderTop: '1px solid var(--c-border)', padding: 12 }}>
                <div style={{ marginBottom: 8 }}>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                    {QUICK.map((q) => (
                      <button key={q} type="button" onClick={() => setContext(q)}
                        style={{ fontSize: 11, padding: '4px 9px', borderRadius: 999, cursor: 'pointer', fontFamily: 'Inter,sans-serif',
                          border: `1px solid ${context === q ? 'var(--c-border-bright)' : 'var(--c-border)'}`,
                          background: context === q ? 'var(--c-primary-glow)' : 'transparent', color: context === q ? 'var(--c-primary)' : 'var(--c-text-2)' }}>
                        {q}
                      </button>
                    ))}
                  </div>
                  <input value={context} onChange={(e) => setContext(e.target.value)}
                    placeholder="Context for the AI (optional): what actually happened, what you want this email to do…"
                    style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', fontSize: 12.5, padding: '8px 12px' }} />
                </div>
                <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject (auto-filled by AI draft)"
                  style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', marginBottom: 8, fontSize: 12.5, padding: '8px 12px' }} />
                <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={4}
                  placeholder="Write a message, or let AI draft the follow-up…"
                  style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', resize: 'vertical', lineHeight: 1.5 }} />
                {timing && !timing.send_now && (
                  <div style={{ fontSize: 12, color: 'var(--c-text)', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.45)', borderRadius: 8, padding: '7px 10px', marginTop: 8, lineHeight: 1.45 }}>
                    ⏳ <b>Hold this one:</b> {timing.advice}
                  </div>
                )}
                {why && why.note_conflict && (
                  <div style={{ fontSize: 12, color: 'var(--c-text)', background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.40)', borderRadius: 8, padding: '7px 10px', marginTop: 8, lineHeight: 1.45 }}>
                    ⚠ <b>Your note vs the history:</b> {why.note_conflict}
                  </div>
                )}
                {why && (why.likely_reason || why.make_it_easy || why.where_it_stands) && (
                  <div style={{ fontSize: 11.5, color: 'var(--c-text-2)', background: 'var(--c-surface)', border: '1px solid var(--c-border)', borderRadius: 8, padding: '7px 10px', marginTop: 8, lineHeight: 1.45 }}>
                    {why.they_said && <div><b>They said:</b> {why.they_said}</div>}
                    {why.where_it_stands && <div><b>Where it stands:</b> {why.where_it_stands}</div>}
                    {why.likely_reason && <div><b>Likely reason:</b> {why.likely_reason}</div>}
                    {why.make_it_easy && <div><b>Make it easy:</b> {why.make_it_easy}</div>}
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, color: 'var(--c-text-3)' }}>
                    sends from <b>{thread?.owner_mailbox || '…'}</b> with {thread?.rep_name || 'the rep'}'s signature
                    {stage && <Chip tone="purple" style={{ marginLeft: 8, fontSize: 10 }}>{stage}</Chip>}
                  </span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Btn small onClick={genDraft} disabled={drafting || thread?.loading}>
                      <Sparkles size={13} /> {drafting ? 'Drafting…' : 'AI draft'}
                    </Btn>
                    <Btn small tone="primary" onClick={sendMsg} disabled={sending || !draft.trim()}>
                      <Send size={13} /> {sending ? 'Sending…' : 'Send'}
                    </Btn>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        )}
      </div>
      <ConfirmDialog
        open={confirmLost}
        danger
        title={`Mark ${activeContact?.company || active} as lost?`}
        body="They will be removed from the Follow-ups list and their pipeline lead moves to Lost. The conversation history stays in the system."
        confirmLabel="Mark lost"
        onConfirm={markLost}
        onCancel={() => setConfirmLost(false)}
      />
      {toasts}
    </div>
  );
};

export default Followups;
